import { HELPDESK_OPEN_STATUSES, HELPDESK_SEARCH_MAX, HELPDESK_SORT_KEYS, HELPDESK_STATUSES, type MeResponse } from "@seagames/contracts";
import { KeysetPager, ListHeader, ListToolbar, useUiStrings } from "@seagames/ui";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { buildingOptionsOptions } from "../../lib/api/accommodation";
import { helpdeskTicketsOptions } from "../../lib/api/helpdesk";
import { type SearchNavigate, useListPage } from "../../lib/list-page/use-list-page";
import { responsibleOrganizationOptionsOptions } from "../../lib/responsible-organizations/queries";
import { EmptyCard } from "../common/empty-card";
import { canHandleHelpdesk, hotelBuildingIds, isHelpdeskReporter } from "./access";
import { InboxIcon } from "./icons";
import { NewTicketModal } from "./new-ticket-modal";
import { useStatusChipOptions } from "./status";
import { HelpdeskStatusGuide } from "./status-guide";
import { TicketModal } from "./ticket-modal";
import { TicketRow } from "./ticket-row";
import type { HelpdeskStatus } from "./types";

/**
 * The Helpdesk tab (her `accHelp`, PLAN §4): one queue, newest first or by
 * priority, Open + In progress by default. URL = state in the API's own keys:
 *   status  comma list; absent = Open + In progress (hers), `any` = every status
 *   sort    newest | priority
 *   lodging_building_id, responsible_organization_id, q
 *   cursor / prev / limit  the keyset page
 * Search applies on Enter, chips on click (platform rule). Who sees which
 * ticket is SQL's business; this page only hides what a role cannot use:
 * a NOC (reporter) has no contingent filter and reports through its rooming
 * page's room list, a HOTEL picks among its own buildings.
 */

const lenient = <T extends z.ZodTypeAny>(schema: T) => schema.optional().catch(undefined);
const PAGE_SIZES = [25, 50, 100] as const;
const PAGE_SIZE_DEFAULT = 25;
const ANY = "any";

export const helpdeskSearchShape = {
	games_id: lenient(z.string().uuid()),
	status: lenient(z.string().min(1).max(60)),
	sort: lenient(z.enum(HELPDESK_SORT_KEYS)),
	lodging_building_id: lenient(z.string().uuid()),
	responsible_organization_id: lenient(z.string().uuid()),
	q: lenient(z.string().trim().min(1).max(HELPDESK_SEARCH_MAX)),
	limit: lenient(z.coerce.number().int().min(1).max(100)),
	cursor: lenient(z.string().min(1).max(300)),
	prev: lenient(z.array(z.string().min(1).max(300)).max(100)),
};
export type HelpdeskSearch = z.infer<z.ZodObject<typeof helpdeskSearchShape>>;

/** URL → the status chips: absent = the default open set, `any` = none picked (every status). */
export function statusesFromUrl(v: string | undefined): HelpdeskStatus[] {
	if (v === undefined) return [...HELPDESK_OPEN_STATUSES];
	if (v === ANY) return [];
	const picked = v.split(",").filter((s): s is HelpdeskStatus => (HELPDESK_STATUSES as readonly string[]).includes(s));
	return picked.length > 0 ? picked : [...HELPDESK_OPEN_STATUSES];
}

function statusesToUrl(v: readonly string[]): string | undefined {
	if (v.length === 0) return ANY;
	const sorted = HELPDESK_STATUSES.filter((s) => v.includes(s));
	const isDefault = sorted.length === HELPDESK_OPEN_STATUSES.length && HELPDESK_OPEN_STATUSES.every((s) => sorted.includes(s));
	return isDefault ? undefined : sorted.join(",");
}

export interface HelpdeskViewProps {
	session: Pick<MeResponse, "active_role" | "group" | "user">;
	gamesId: string;
	search: HelpdeskSearch;
	navigate: SearchNavigate<HelpdeskSearch>;
}

export function HelpdeskView({ session, gamesId, search, navigate }: HelpdeskViewProps) {
	const { t } = useTranslation("helpdesk");
	const { formatNumber } = useUiStrings();
	const handler = canHandleHelpdesk(session);
	const reporter = isHelpdeskReporter(session);
	const hotelIds = hotelBuildingIds(session);
	const statusChips = useStatusChipOptions();
	const [opened, setOpened] = useState<string | null>(null);
	const [creating, setCreating] = useState(false);

	const statuses = statusesFromUrl(search.status);
	const buildings = useQuery(buildingOptionsOptions(gamesId));
	const orgs = useQuery({ ...responsibleOrganizationOptionsOptions(), enabled: handler });
	const buildingChoices = (buildings.data ?? []).filter((b) => hotelIds.length === 0 || hotelIds.includes(b.lodging_building_id));

	const page = useListPage({
		paging: "keyset",
		search,
		navigate,
		listOptions: (q) =>
			helpdeskTicketsOptions({
				games_id: gamesId,
				// None ticked = every status, sent explicitly: an absent `status` is the API's open-only default.
				status: (statuses.length > 0 ? statuses : HELPDESK_STATUSES).join(","),
				sort: search.sort,
				lodging_building_id: search.lodging_building_id,
				responsible_organization_id: reporter ? undefined : search.responsible_organization_id,
				q: search.q,
				cursor: q.cursor,
				limit: q.limit,
			}),
		columnsKey: "helpdesk-tickets",
		pageSizes: PAGE_SIZES,
		defaultPageSize: PAGE_SIZE_DEFAULT,
		summary: {
			showing: (v) => t("list.showing", v),
			showingNoTotal: (v) => t("list.showingNoTotal", v),
			empty: t("list.showingEmpty"),
		},
	});

	const filtered = !!search.q || !!search.lodging_building_id || !!search.responsible_organization_id || search.status !== undefined;
	const clearAll = () => page.patch({ q: undefined, lodging_building_id: undefined, responsible_organization_id: undefined, status: undefined });

	return (
		<div className="flex min-w-0 flex-col gap-[1rem]">
			<ListHeader
				as="h2"
				title={t("page.title")}
				count={page.total === null ? undefined : t("page.count", { count: page.total, n: formatNumber(page.total) })}
				description={reporter ? t("page.introReporter") : t("page.intro")}
				actionLabel={handler ? t("create.open") : undefined}
				onAction={handler ? () => setCreating(true) : undefined}
			/>

			<ListToolbar
				search={search.q ?? ""}
				onSearch={(v) => page.patch({ q: v || undefined })}
				searchPlaceholder={reporter ? t("list.searchReporter") : t("list.search")}
				sort={{
					value: search.sort ?? "newest",
					options: [
						{ value: "newest", label: t("list.sortNewest") },
						{ value: "priority", label: t("list.sortPriority") },
					],
					onChange: (v) => page.patch({ sort: v === "newest" ? undefined : (v as "priority") }),
				}}
				end={<HelpdeskStatusGuide />}
				chips={[
					{
						kind: "options",
						key: "status",
						label: t("list.statusChips"),
						multiple: true,
						options: statusChips,
						value: statuses,
						onChange: (v) => page.patch({ status: statusesToUrl(v) }),
					},
					...(buildingChoices.length > 1
						? [
								{
									kind: "menu" as const,
									key: "building",
									label: t("list.building"),
									options: buildingChoices.map((b) => ({ value: b.lodging_building_id, label: b.name })),
									value: search.lodging_building_id ?? null,
									onChange: (v: string | null) => page.patch({ lodging_building_id: v ?? undefined }),
									searchPlaceholder: t("common.search"),
								},
							]
						: []),
					...(handler && (orgs.data?.length ?? 0) > 0
						? [
								{
									kind: "menu" as const,
									key: "org",
									label: t("list.contingent"),
									options: (orgs.data ?? []).map((o) => ({ value: o.responsible_organization_id, label: o.name })),
									value: search.responsible_organization_id ?? null,
									onChange: (v: string | null) => page.patch({ responsible_organization_id: v ?? undefined }),
									searchPlaceholder: t("common.search"),
								},
							]
						: []),
				]}
				active={filtered}
				onClearAll={clearAll}
			/>

			{page.list.isError ? (
				<EmptyCard
					tone="bad"
					title={t("list.loadFailed")}
					actions={
						<button type="button" className="btn btn-sm btn-line" onClick={() => void page.list.refetch()}>
							{t("common.retry")}
						</button>
					}
				>
					{page.table.error?.message}
				</EmptyCard>
			) : page.list.isPending ? (
				<div className="grid gap-[0.5rem]" aria-busy>
					<div className="skeleton h-[5rem] rounded-[0.625rem]" />
					<div className="skeleton h-[5rem] rounded-[0.625rem]" />
					<div className="skeleton h-[5rem] rounded-[0.625rem]" />
				</div>
			) : page.rows.length === 0 ? (
				<EmptyCard
					icon={<InboxIcon />}
					title={search.q || search.lodging_building_id || search.responsible_organization_id ? t("list.emptyFilteredTitle") : search.status === undefined ? t("list.emptyOpenTitle") : t("list.emptyTitle")}
					actions={
						filtered ? (
							<button type="button" className="btn btn-sm btn-line" onClick={clearAll}>
								{t("common.clearFilters")}
							</button>
						) : undefined
					}
				>
					{reporter ? t("list.emptyReporter") : t("list.emptyDescription")}
				</EmptyCard>
			) : (
				<>
					<ul className={`grid gap-[0.5rem] ${page.list.isFetching ? "opacity-70 transition-opacity" : "transition-opacity"}`}>
						{page.rows.map((r) => (
							<li key={r.helpdesk_ticket_id}>
								<TicketRow row={r} onOpen={() => setOpened(r.helpdesk_ticket_id)} showOrg={!reporter} />
							</li>
						))}
					</ul>
					<KeysetPager {...page.pager} position="bottom" />
				</>
			)}

			{opened && <TicketModal ticketId={opened} onClose={() => setOpened(null)} />}
			{creating && <NewTicketModal mode="desk" gamesId={gamesId} onClose={() => setCreating(false)} fixedBuildingIds={hotelIds.length > 0 ? hotelIds : undefined} />}
		</div>
	);
}
